/**
 * Quick actions catalog — declarative metadata for the dashboard quick links.
 *
 * Data only (kept out of the component file so fast-refresh stays intact).
 * 'dialog' actions open a modal on top of the dashboard (handled by
 * DashboardPage); 'navigate' actions route away. Array order is the default
 * display order.
 */
import type { SvgIconComponent } from '@mui/icons-material';
import type { Theme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BusinessIcon from '@mui/icons-material/Business';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import InventoryIcon from '@mui/icons-material/Inventory';
import MessageIcon from '@mui/icons-material/Message';
import SearchIcon from '@mui/icons-material/Search';

/** Dialogs that a quick action can open in place on the dashboard. */
export type DashboardDialogId = 'workOrder' | 'quote' | 'person' | 'company' | 'search';

export interface QuickActionDef {
  id: string;
  /** i18n key within the `dashboard` namespace. */
  labelKey: string;
  Icon: SvgIconComponent;
  color: (theme: Theme) => string;
  /** 'dialog' opens a modal in place; 'navigate' routes away. */
  kind: 'dialog' | 'navigate';
  dialog?: DashboardDialogId;
  href?: string;
}

export const QUICK_ACTIONS: QuickActionDef[] = [
  {
    id: 'new-work-order',
    labelKey: 'quickActions.newWorkOrder',
    Icon: AddIcon,
    color: (t) => t.palette.primary.light,
    kind: 'dialog',
    dialog: 'workOrder',
  },
  {
    id: 'new-quotation',
    labelKey: 'quickActions.newQuotation',
    Icon: RequestQuoteIcon,
    color: (t) => t.palette.info.light,
    kind: 'dialog',
    dialog: 'quote',
  },
  {
    id: 'view-calendar',
    labelKey: 'quickActions.calendar',
    Icon: CalendarMonthIcon,
    color: (t) => t.palette.secondary.light,
    kind: 'navigate',
    href: '/calendar',
  },
  {
    id: 'work-orders',
    labelKey: 'quickActions.workOrders',
    Icon: AssignmentIcon,
    color: (t) => t.palette.warning.dark,
    kind: 'navigate',
    href: '/workorders',
  },
  {
    id: 'new-person',
    labelKey: 'quickActions.newPerson',
    Icon: PersonAddIcon,
    color: (t) => t.palette.success.main,
    kind: 'dialog',
    dialog: 'person',
  },
  {
    id: 'new-company',
    labelKey: 'quickActions.newCompany',
    Icon: BusinessIcon,
    color: (t) => t.palette.success.dark,
    kind: 'dialog',
    dialog: 'company',
  },
  {
    id: 'vehicles',
    labelKey: 'quickActions.vehicles',
    Icon: DirectionsCarIcon,
    color: (t) => t.palette.error.light,
    kind: 'navigate',
    href: '/vehicles',
  },
  {
    id: 'inventory',
    labelKey: 'quickActions.inventory',
    Icon: InventoryIcon,
    color: (t) => t.palette.success.dark,
    kind: 'navigate',
    href: '/inventory',
  },
  {
    id: 'messages',
    labelKey: 'quickActions.messages',
    Icon: MessageIcon,
    color: (t) => t.palette.secondary.main,
    kind: 'navigate',
    href: '/messages',
  },
  {
    id: 'search',
    labelKey: 'quickActions.search',
    Icon: SearchIcon,
    color: (t) => t.palette.text.secondary,
    kind: 'dialog',
    dialog: 'search',
  },
];

export const QUICK_ACTION_IDS = QUICK_ACTIONS.map((a) => a.id);
